import React, { useState } from 'react';
import { Accordion } from './ui/Accordion';
import { Zone, Configuration } from '../utils/types';
interface ZoneDetailsProps {
  zone: Zone;
  configuration: Configuration;
}
export function ZoneDetails({
  zone,
  configuration
}: ZoneDetailsProps) {
  const [deltaT, setDeltaT] = useState(5);
  const [floorTemp, setFloorTemp] = useState(26);
  const [maxLoopLength, setMaxLoopLength] = useState(90);
  const [cc, setCC] = useState(200);
  // Calculate flow based on parameters (simplified for demo)
  const flow = (zone.area * 0.1 * deltaT / 10).toFixed(1);
  const pressure = (zone.area * 0.1 * 0.8).toFixed(1);
  const loops = Math.ceil(zone.area / 10);
  const loopLength = Math.ceil(zone.area * 78 / 38);
  // Determine pressure status color
  const getPressureStatusColor = (pressure: number) => {
    if (pressure < 4) return 'text-green-600 bg-green-50';
    if (pressure < 8) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };
  return <Accordion title={`Zone-detaljer: ${zone.name} (${zone.area} m²)`} data-id="element-272">
      <div className="space-y-6" data-id="element-273">
        {/* Selected components for this zone */}
        <div className="bg-gray-50 rounded-xl p-4" data-id="element-274">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-275">
            Valgte komponenter
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-276">
            <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-277">
              <div className="text-xs text-gray-500 mb-1" data-id="element-278">Manifold</div>
              <div className="text-sm" data-id="element-279">Roth gulvvarmefordeler 6 afgr.</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-280">
              <div className="text-xs text-gray-500 mb-1" data-id="element-281">Rør</div>
              <div className="text-sm" data-id="element-282">Roth X-PERT S5® 16 mm, c/c 200</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-283">
              <div className="text-xs text-gray-500 mb-1" data-id="element-284">Termostat</div>
              <div className="text-sm" data-id="element-285">CompA WiFi-termostat M30</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-286">
              <div className="text-xs text-gray-500 mb-1" data-id="element-287">Pumpe</div>
              <div className="text-sm" data-id="element-288">CompB cirkulationspumpe</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200" data-id="element-289">
              <div className="text-xs text-gray-500 mb-1" data-id="element-290">Ventilation</div>
              <div className="text-sm" data-id="element-291">Ikke valgt</div>
            </div>
          </div>
        </div>
        {/* Parameters */}
        <div data-id="element-292">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-293">Parametre</h3>
          <div className="space-y-4" data-id="element-294">
            <div data-id="element-295">
              <div className="flex justify-between mb-1" data-id="element-296">
                <label className="text-xs text-gray-600" data-id="element-297">
                  ΔT (°C): {deltaT}
                </label>
                <span className="text-xs text-gray-600" data-id="element-298">{deltaT}</span>
              </div>
              <input type="range" min="3" max="10" value={deltaT} onChange={e => setDeltaT(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500" data-id="element-299" />
            </div>
            <div data-id="element-300">
              <div className="flex justify-between mb-1" data-id="element-301">
                <label className="text-xs text-gray-600" data-id="element-302">
                  T-floor (°C): {floorTemp}
                </label>
                <span className="text-xs text-gray-600" data-id="element-303">{floorTemp}</span>
              </div>
              <input type="range" min="20" max="32" value={floorTemp} onChange={e => setFloorTemp(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500" data-id="element-304" />
            </div>
            <div data-id="element-305">
              <div className="flex justify-between mb-1" data-id="element-306">
                <label className="text-xs text-gray-600" data-id="element-307">
                  Max kredslængde (m): {maxLoopLength}
                </label>
                <span className="text-xs text-gray-600" data-id="element-308">{maxLoopLength}</span>
              </div>
              <input type="range" min="60" max="120" value={maxLoopLength} onChange={e => setMaxLoopLength(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500" data-id="element-309" />
            </div>
            <div data-id="element-310">
              <div className="flex justify-between mb-1" data-id="element-311">
                <label className="text-xs text-gray-600" data-id="element-312">c/c (mm): {cc}</label>
                <span className="text-xs text-gray-600" data-id="element-313">{cc}</span>
              </div>
              <input type="range" min="100" max="300" step="25" value={cc} onChange={e => setCC(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-500" data-id="element-314" />
            </div>
          </div>
          <button className="mt-4 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm transition-colors" data-id="element-315">
            Rekalkuler flow
          </button>
        </div>
        {/* Results */}
        <div data-id="element-316">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-317">Resultater</h3>
          <div className="flex flex-wrap gap-3" data-id="element-318">
            <div className="bg-blue-50 px-3 py-1.5 rounded-lg text-sm text-blue-700" data-id="element-319">
              Q {flow} l/min
            </div>
            <div className={`px-3 py-1.5 rounded-lg text-sm ${getPressureStatusColor(parseFloat(pressure))}`} data-id="element-320">
              AP {pressure} kPa
            </div>
            <div className="bg-gray-100 px-3 py-1.5 rounded-lg text-sm text-gray-700" data-id="element-321">
              Kredse {loops} (L={loopLength} m)
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500" data-id="element-322">
            Indikativ beregning. Kontakt teknisk afdeling for præcis
            dimensionering.
          </div>
        </div>
      </div>
    </Accordion>;
}